import { useEffect, useState } from "react";
import { Autocomplete, Box, Card, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { apiConstants, post } from "../../common/constants";

// Lightweight entry point for users who only have the MERCHANT_COURIER_ALLOCATION role.
// Pick a merchant -> open its detail page; the detail page restricts these users to the
// Courier Allocation tab on its own (see `allocationOnly` in `merchant details/index.js`).
export default function CourierAllocationPicker() {
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    post(apiConstants.MERCHANT_SEARCH_CACHED, {
      skipCount: true,
      limit: 1000,
      includes: "name,companyName,merchantId,id",
    })
      .then((res) => {
        const list = res?.data?.data || [];
        // Match the merchant-dropdown convention used elsewhere (e.g. MerchantWallet):
        // "name (companyName)", falling back to name / companyName / merchantId.
        // Skip records with nothing identifiable so we don't render blank "—" rows.
        const opts = list
          .map((m) => {
            const name = (m.name || "").trim();
            const company = (m.companyName || "").trim();
            if (!name && !company && !m.merchantId) return null;
            const label =
              name && company ? `${name} (${company})` : name || company || `#${m.merchantId}`;
            return { value: m.id, label };
          })
          .filter(Boolean);
        opts.sort((a, b) => a.label.localeCompare(b.label));
        setMerchants(opts);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (_, option) => {
    if (option?.value) {
      navigate(`/merchant/${option.value}?openModal=true&id=${option.value}`);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card
        sx={{
          p: 3,
          borderRadius: 3,
          maxWidth: 560,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
          Courier Allocation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a merchant to manage its courier allocation rules.
        </Typography>
        <Autocomplete
          options={merchants}
          loading={loading}
          onChange={handleSelect}
          getOptionLabel={(o) => o.label || ""}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Merchant"
              placeholder="Search merchant..."
              size="small"
            />
          )}
        />
      </Card>
    </Box>
  );
}
